/**
 * Mailing Service - API client for mailing address endpoints
 */

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('auth_token')
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  }
}

export interface AddressData {
  full_name: string
  address_line1: string
  address_line2?: string
  city: string
  state: string
  zip_code: string
}

export interface MailingAddress {
  id: string
  user_id: string
  full_name: string
  address_line1: string
  address_line2: string | null
  city: string
  state: string
  zip_code: string
  country: string
  is_validated: boolean
  normalized_address_line1: string | null
  normalized_city: string | null
  normalized_state: string | null
  normalized_zip_code: string | null
  is_current: boolean
  status: 'pending' | 'approved' | 'superseded' | 'deleted'
  submitted_at: string
  reviewed_at: string | null
  created_at: string
  updated_at: string
}

export interface ValidationResult {
  valid: boolean
  dpv_match_code: string | null
  normalized: {
    address_line1: string
    city: string
    state: string
    zip_code: string
  } | null
  reason: string | null
}

export interface SubmitResult {
  success: boolean
  address?: MailingAddress
  status?: string
  normalized?: ValidationResult['normalized']
  validation_note?: string | null
  validation_error?: boolean
  reason?: string
}

/**
 * Get current user's mailing address
 */
export async function getAddress(): Promise<MailingAddress | null> {
  const response = await fetch(`${API_URL}/api/mailing/address`, {
    headers: getAuthHeaders()
  })

  if (!response.ok) {
    throw new Error('Failed to fetch address')
  }

  const data = await response.json()
  return data.address || null
}

/**
 * Submit a new mailing address
 */
export async function submitAddress(addressData: AddressData): Promise<SubmitResult> {
  const response = await fetch(`${API_URL}/api/mailing/address`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(addressData)
  })

  const data = await response.json()

  if (response.status === 422) {
    return {
      success: false,
      validation_error: true,
      reason: data.reason,
      normalized: data.normalized
    }
  }

  if (!response.ok) {
    throw new Error(data.error || 'Failed to submit address')
  }

  return data
}

/**
 * Update existing mailing address
 */
export async function updateAddress(addressData: AddressData): Promise<SubmitResult> {
  const response = await fetch(`${API_URL}/api/mailing/address`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(addressData)
  })

  const data = await response.json()

  if (response.status === 422) {
    return {
      success: false,
      validation_error: true,
      reason: data.reason,
      normalized: data.normalized
    }
  }

  if (!response.ok) {
    throw new Error(data.error || 'Failed to update address')
  }

  return data
}

/**
 * Validate an address without saving
 */
export async function validateAddress(addressData: Omit<AddressData, 'full_name'>): Promise<ValidationResult> {
  const response = await fetch(`${API_URL}/api/mailing/validate`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(addressData)
  })

  if (!response.ok) {
    throw new Error('Failed to validate address')
  }

  return response.json()
}

export interface AddressSuggestion {
  street_line: string
  secondary: string
  city: string
  state: string
  zipcode: string
  entries: number
}

/**
 * Autocomplete address - returns suggestions as user types
 */
export async function autocompleteAddress(search: string): Promise<AddressSuggestion[]> {
  if (!search || search.trim().length < 3) return []

  const response = await fetch(`${API_URL}/api/mailing/autocomplete?search=${encodeURIComponent(search.trim())}`, {
    headers: getAuthHeaders()
  })

  if (!response.ok) return []

  const data = await response.json()
  return data.suggestions || []
}

/**
 * Get user's address history
 */
export async function getAddressHistory(): Promise<MailingAddress[]> {
  const response = await fetch(`${API_URL}/api/mailing/history`, {
    headers: getAuthHeaders()
  })

  if (!response.ok) {
    throw new Error('Failed to fetch address history')
  }

  const data = await response.json()
  return data.history || []
}
